import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Same technique → tutorial map as GamePage's walkthrough panel.
const TECHNIQUE_TUTORIAL_ID = {
  'Naked Single': 1, 'Hidden Single': 2, 'Naked Pair': 3, 'Hidden Pair': 4,
  'Naked Triple': 5, 'Hidden Triple': 6, 'X-Wing': 7, 'Pointing Pair': 8,
  'Box-Line Reduction': 9, 'Naked Quad': 10, 'Hidden Quad': 11, 'Swordfish': 12,
  'XY-Wing': 13, 'Simple Coloring': 14, 'AIC': 15, 'XYZ-Wing': 15,
  'ALS-XZ': 16, 'Forcing Chain': 17,
};

// Lightweight solver-only board (no editing, no pencil marks — just
// shows the current state of the walkthrough with the pending step's
// target cell highlighted in amber).
const SolverBoard = React.memo(function SolverBoard({ grid, originalGrid, pendingPlacement, references }) {
  return (
    <div className="solver-board" data-testid="solver-board">
      {grid.map((row, r) => (
        <div key={r} className="solver-board-row">
          {row.map((val, c) => {
            const isClue = originalGrid[r][c] !== 0;
            const isPending =
              pendingPlacement &&
              pendingPlacement.row === r &&
              pendingPlacement.col === c;
            const ref = references.find((rc) => rc.row === r && rc.col === c);
            const cls = [
              'solver-cell',
              isClue ? 'solver-cell-clue' : '',
              isPending ? 'solver-cell-pending' : '',
              ref ? `solver-cell-ref-${ref.role}` : '',
              c % 3 === 2 && c < 8 ? 'solver-cell-thick-right' : '',
              r % 3 === 2 && r < 8 ? 'solver-cell-thick-bottom' : '',
            ].filter(Boolean).join(' ');
            return (
              <div key={c} className={cls}>
                {val !== 0 ? (
                  <span className={isClue ? 'solver-clue-digit' : 'solver-fill-digit'}>{val}</span>
                ) : isPending ? (
                  <span className="solver-pending-digit">{pendingPlacement.value}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});

export default function SolverPage() {
  const [rawInput, setRawInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  // The original grid the user submitted (frozen on submit so subsequent
  // walkthrough application doesn't mutate it).
  const originalGrid = useMemo(() => {
    if (!result?.steps) return null;
    return result._origGrid;
  }, [result]);

  // Current grid shown — applies all PRIOR walkthrough placements but
  // NOT the current step's placement (preview-then-commit, same as in-game Solve).
  const currentGrid = useMemo(() => {
    if (!result?.steps || !originalGrid) return null;
    const g = originalGrid.map((row) => [...row]);
    for (let i = 0; i < stepIndex; i++) {
      const s = result.steps[i];
      if (s.type === 'placement') g[s.row][s.col] = s.value;
    }
    return g;
  }, [result, stepIndex, originalGrid]);

  const currentStep = result?.steps?.[stepIndex];
  const pendingPlacement =
    currentStep && currentStep.type === 'placement'
      ? { row: currentStep.row, col: currentStep.col, value: currentStep.value }
      : null;
  const references = currentStep?.references || [];

  const handleAnalyze = useCallback(async () => {
    if (!rawInput.trim()) {
      toast.error('Paste a puzzle first');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/solver/analyze`, {
        grid: rawInput,
      });
      // Rebuild the original grid from the user's input so we can render
      // it (the backend returns steps + solution but not the original).
      const cleaned = rawInput
        .split('')
        .map((c) => (c.match(/[1-9]/) ? c : c === '.' || c === '0' ? '0' : ''))
        .join('');
      const orig = [];
      for (let r = 0; r < 9; r++) {
        const row = [];
        for (let c = 0; c < 9; c++) row.push(parseInt(cleaned[r * 9 + c], 10) || 0);
        orig.push(row);
      }
      setResult({ ...data, _origGrid: orig });
      setStepIndex(0);
      if (data.banked) {
        toast.success(`Solved! Filed at L${data.level} (${data.difficulty_name}). Thanks for sharing.`);
      } else {
        toast.success(`Solved! L${data.level} (${data.difficulty_name})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to analyze');
    } finally {
      setSubmitting(false);
    }
  }, [rawInput]);

  const stepLabel = result
    ? stepIndex >= result.steps.length
      ? `Solve complete · ${result.steps.length}/${result.steps.length}`
      : `Step ${stepIndex + 1} / ${result.steps.length}`
    : '';

  return (
    <div className="solver-page" data-testid="solver-page">
      <div className="solver-header">
        <Link to="/" className="solver-back-link" data-testid="solver-back-link">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <h1 className="solver-title">Sudoku Solver</h1>
        <p className="solver-subtitle">
          Paste any puzzle. We'll solve it with pure logic and walk you through every step.
        </p>
      </div>

      {!result && (
        <div className="solver-input-card" data-testid="solver-input-card">
          <label className="solver-input-label">
            Paste your grid (81 digits, use 0 or . for empty cells; spaces and newlines ignored):
          </label>
          <textarea
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            rows={9}
            spellCheck={false}
            className="solver-input-textarea"
            data-testid="solver-input-textarea"
            placeholder={`530 070 000\n600 195 000\n098 000 060\n800 060 003\n400 803 001\n700 020 006\n060 000 280\n000 419 005\n000 080 079`}
          />
          <Button
            onClick={handleAnalyze}
            disabled={submitting || !rawInput.trim()}
            data-testid="solver-analyze-btn"
            className="solver-analyze-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {submitting ? 'Analyzing…' : 'Analyze & solve'}
          </Button>
        </div>
      )}

      {result && (
        <div className="solver-result" data-testid="solver-result">
          <div className="solver-result-header">
            <div>
              <span className="solver-result-level">L{result.level}</span>
              <span className="solver-result-name">{result.difficulty_name}</span>
            </div>
            <div className="solver-result-meta">
              {result.cells_given} clues · {result.steps.length} steps
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setResult(null); setRawInput(''); }}
              data-testid="solver-new-btn"
            >
              New puzzle
            </Button>
          </div>

          <SolverBoard
            grid={currentGrid}
            originalGrid={originalGrid}
            pendingPlacement={pendingPlacement}
            references={references}
          />

          <div className="solver-step-panel" data-testid="solver-step-panel">
            <div className="solver-step-header">
              <span className="solver-step-label">{stepLabel}</span>
              <div className="solver-step-progress">
                <div
                  className="solver-step-progress-bar"
                  style={{
                    width: `${Math.min(100, (stepIndex / result.steps.length) * 100)}%`,
                  }}
                />
              </div>
            </div>

            {currentStep && (
              <>
                <div className="solver-step-tech">
                  <span className="solver-step-tech-pill">{currentStep.technique}</span>
                  <span className="solver-step-tech-kind">
                    {currentStep.type === 'placement' ? 'Value Placement' : 'Candidate Elimination'}
                  </span>
                  {TECHNIQUE_TUTORIAL_ID[currentStep.technique] && (
                    <a
                      href={`/tutorials/${TECHNIQUE_TUTORIAL_ID[currentStep.technique]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="solver-step-teach-link"
                      data-testid="solver-teach-link"
                    >
                      <BookOpen className="w-3 h-3" /> Teach me
                    </a>
                  )}
                </div>
                <p className="solver-step-explanation">{currentStep.explanation}</p>
              </>
            )}

            <div className="solver-step-controls">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                disabled={stepIndex === 0}
                data-testid="solver-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button
                size="sm"
                onClick={() => setStepIndex((i) => Math.min(result.steps.length, i + 1))}
                disabled={stepIndex >= result.steps.length}
                data-testid="solver-next-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
